import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { getBranches, createBranch, updateBranch } from '../api';
import type { Branch } from '../types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { FormField } from '@/components/ui/FormField';
import { Input, Textarea } from '@/components/ui/Input';
import { RowActionsMenu } from '@/components/ui/RowActionsMenu';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { toast } from '@/hooks/useToast';
import { Dash, SettingsToolbar, filterByStatus, matchesSearch, type StatusFilter } from '../shared/SettingsToolbar';

const EMPTY = { code: '', name: '', address: '', phone: '', gstin: '' };
type Form = typeof EMPTY;

/**
 * Admin → Facility → Branches. Patients are shared across every branch;
 * visits, queue, appointments, admissions, bills and inventory belong to one.
 * The default branch is what a user with no branch preference lands on.
 *
 * List + side drawer + ⋯ row menu (Admin_Panel_Wireframe.html, D4). The
 * page's own description comes from the Admin shell, so this screen no
 * longer repeats it.
 */
export default function BranchesTab() {
  const [branches, setBranches] = useState<Branch[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [editing, setEditing] = useState<Branch | 'new' | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<Branch | null>(null);

  const load = useCallback(() => getBranches().then((b: Branch[]) => { setBranches(b); setLoadError(''); }).catch((e: any) => setLoadError(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast({ title: ok, variant: 'success' }); await load(); }
    catch (e: any) { toast({ title: 'Not saved', description: e.message, variant: 'danger' }); }
  };

  const rows = useMemo(
    () => filterByStatus(branches ?? [], status).filter((b) => matchesSearch(search, b.code, b.name, b.address, b.phone, b.gstin)),
    [branches, status, search]
  );

  const columns: DataTableColumn<Branch>[] = [
    { key: 'code', header: 'Code', widthClassName: 'w-[90px]', sortable: true, sortValue: (b) => b.code,
      render: (b) => <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11.5px] font-bold">{b.code}</span> },
    { key: 'name', header: 'Name', sortable: true, sortValue: (b) => b.name,
      render: (b) => (
        <span className="flex items-center gap-2">
          <span className="font-semibold">{b.name}</span>
          {b.is_default && <Badge variant="info">Default</Badge>}
        </span>
      ) },
    { key: 'address', header: 'Address', render: (b) => b.address || <Dash /> },
    { key: 'phone', header: 'Phone', widthClassName: 'w-[140px]', render: (b) => b.phone || <Dash /> },
    { key: 'gstin', header: 'GSTIN', widthClassName: 'w-[170px]', render: (b) => (b.gstin ? <span className="font-mono text-[12px]">{b.gstin}</span> : <Dash />) },
    { key: 'status', header: 'Status', widthClassName: 'w-[130px]',
      render: (b) => !b.active
        ? <Badge variant="neutral">Inactive</Badge>
        // The address prints on this branch's invoices and letterhead — a
        // missing one is worth flagging, not just leaving blank.
        : !b.address
          ? <Badge variant="warn">No address</Badge>
          : <Badge variant="success">Active</Badge> },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', widthClassName: 'w-[56px]',
      render: (b) => (
        <RowActionsMenu
          label={`Actions for ${b.name}`}
          actions={[
            { label: 'Edit details', onSelect: () => setEditing(b) },
            ...(!b.is_default && b.active ? [{ label: 'Make default', onSelect: () => act(() => updateBranch(b.id, { is_default: true }), `${b.name} is now the default branch`) }] : []),
            b.active
              ? { label: 'Deactivate…', danger: true, disabled: b.is_default, hint: b.is_default ? 'Make another branch the default first' : undefined, onSelect: () => setConfirmDeactivate(b) }
              : { label: 'Reactivate', onSelect: () => act(() => updateBranch(b.id, { active: true }), `${b.name} reactivated`) },
          ]}
        />
      ) },
  ];

  return (
    <div>
      <SettingsToolbar
        search={search}
        onSearch={setSearch}
        placeholder="Search branches"
        status={status}
        onStatus={setStatus}
        count={branches ? `${rows.length} branch${rows.length === 1 ? '' : 'es'}` : undefined}
        action={<Button onClick={() => setEditing('new')}><Plus className="h-4 w-4" /> Add branch</Button>}
      />
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(b) => b.id}
        isLoading={!branches && !loadError}
        isError={!!loadError}
        errorMessage={loadError}
        onRetry={load}
        emptyLabel={search ? 'No branches match' : status === 'inactive' ? 'No inactive branches' : 'No branches yet'}
      />
      <p className="mt-2 text-[12px] text-muted-foreground">
        The code is printed in visit and (per-branch mode) invoice numbers, e.g. VIS-KGY-20261020-0001, and can’t be changed once used.
      </p>

      <BranchSheet
        branch={editing}
        onClose={() => setEditing(null)}
        onSaved={async (msg) => { setEditing(null); toast({ title: msg, variant: 'success' }); await load(); }}
      />

      <ConfirmDialog
        open={!!confirmDeactivate}
        onOpenChange={(o) => { if (!o) setConfirmDeactivate(null); }}
        title={`Deactivate ${confirmDeactivate?.name ?? ''}?`}
        description="Staff can no longer switch to this branch and no new visits, bills or stock can be recorded against it. Its existing records stay as they are, and you can reactivate it later."
        confirmLabel="Deactivate"
        onConfirm={() => { const b = confirmDeactivate; if (b) act(() => updateBranch(b.id, { active: false }), `${b.name} deactivated`); }}
      />
    </div>
  );
}

function BranchSheet({ branch, onClose, onSaved }: { branch: Branch | 'new' | null; onClose: () => void; onSaved: (msg: string) => void }) {
  const isNew = branch === 'new';
  const [form, setForm] = useState<Form>(EMPTY);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!branch) return;
    setError(''); setTouched(false);
    setForm(branch === 'new' ? EMPTY : {
      code: branch.code, name: branch.name, address: branch.address ?? '', phone: branch.phone ?? '', gstin: branch.gstin ?? '',
    });
  }, [branch]);

  const set = (k: keyof Form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const codeError = touched && !form.code.trim() ? 'Enter a short code' : undefined;
  const nameError = touched && !form.name.trim() ? 'Enter the branch name' : undefined;

  const save = async () => {
    setTouched(true);
    if (!form.name.trim() || (isNew && !form.code.trim())) return;
    setSaving(true); setError('');
    try {
      const fields = { name: form.name.trim(), address: form.address.trim(), phone: form.phone.trim(), gstin: form.gstin.trim().toUpperCase() };
      if (isNew) {
        await createBranch({ code: form.code.trim().toUpperCase(), ...fields });
        onSaved(`${fields.name} added`);
      } else if (branch) {
        await updateBranch(branch.id, fields);
        onSaved(`${fields.name} saved`);
      }
    } catch (e: any) {
      setError(e.message);
    }
    setSaving(false);
  };

  return (
    <Sheet open={!!branch} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{isNew ? 'Add branch' : 'Edit branch'}</SheetTitle>
          <SheetDescription>{isNew ? 'A new site of this hospital.' : `Branch ${typeof branch === 'object' && branch ? branch.code : ''}`}</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {error && <div className="rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">{error}</div>}
          <FormField label="Branch code" required error={codeError}
            hint={isNew ? '2–6 letters, e.g. KGY. Printed in visit and invoice numbers — it can’t be changed once used.' : 'The code can’t be changed after the branch is created.'}>
            <Input value={form.code} onChange={set('code')} maxLength={10} disabled={!isNew} placeholder="e.g. KGY" className="font-mono uppercase placeholder:normal-case placeholder:font-sans" autoFocus={isNew} />
          </FormField>
          <FormField label="Branch name" required error={nameError}>
            <Input value={form.name} onChange={set('name')} placeholder="e.g. Kangeyam" autoFocus={!isNew} />
          </FormField>
          <FormField label="Address" hint="Printed on this branch’s invoices and letterhead.">
            <Textarea value={form.address} onChange={set('address')} rows={3} placeholder="Street, town, PIN" />
          </FormField>
          <FormField label="Phone">
            <Input value={form.phone} onChange={set('phone')} inputMode="tel" placeholder="+91" />
          </FormField>
          <FormField label="GSTIN" hint="Optional. Leave blank to use the hospital’s GSTIN from Billing Settings.">
            <Input value={form.gstin} onChange={set('gstin')} maxLength={15} placeholder="15 characters" className="font-mono uppercase placeholder:normal-case placeholder:font-sans" />
          </FormField>
          {isNew && (
            <p className="text-[12px] text-muted-foreground">To make it the default branch, use “Make default” in its row menu after saving.</p>
          )}
        </SheetBody>
        <SheetFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={saving}>{isNew ? 'Add branch' : 'Save changes'}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

